/* realsport match api */
import axios from 'crypto-fetch/axios.js'
import xfetch from '@ms/fetch'
import cacheResponse from '@ms/fetch/cache'
import { controller } from '@ms/fetch/cancel'

/**
 * Fetches the live stream information for a specific match event.
 *
 * @param {string} [eventId=''] - The ID of the event to fetch the live stream for. Defaults to an empty string if not provided.
 * @returns {Promise<any>} A promise that resolves with the live stream data for the specified event.
 */
export const fetchMatchLiveStream = (eventId = '') => {
  return xfetch(`/facts-center/query/frontend/live-channel/event?eventId=${eventId}`)
}

// 查询 market guidance
export async function fetchMarketGuide({ sportId, marketId, product = 3 }) {
  const { data } = await xfetch(`/facts-center/query/frontend/market-info/market-guidance?sportId=${sportId}&marketId=${marketId}&product=${product}`)
  return data || ''
}

export const fetchAllSports = async () => {
  return cacheResponse(
    'sports-all',
    xfetch('/facts-center/query/frontend/sports').then(({ error, data }) => {
      if (error) return null
      return data.sports.map(({ sportId, sportName }) => ({ sportId, sportName }))
    })
  )
}

/**
 * 获取当前可投注的体育列表以及对应的比赛个数
 * @param  {Number} product  指定Live或者Prematch，live为1，prematch为3，不传就是所有的
 * @return {Promise}         data
 */
export const fetchSportsProduct = (product) => {
  const params = product ? `?product=${product}` : ''
  return xfetch(`/facts-center/query/frontend/sports-a-z-menu${params}`)
}

/**
 * Fetches live sports data from the server.
 *
 * @returns {Promise} A promise that resolves to the response of the fetch request.
 */
export const fetchLiveSports = () => {
  const key = 'liveSports'
  controller.abort(key)
  const signal = controller.create(key)
  return xfetch('/facts-center/query/frontend/live-matches/sports', { signal })
}

/**
 * Fetches the count of live matches for a given sport.
 *
 * @param {string} [sportId=''] - The ID of the sport to fetch live matches count for. Defaults to an empty string.
 * @returns {Promise} - A promise that resolves to the count of live matches.
 */
export const fetchLiveMatchesCount = (sportId = '') => {
  return xfetch(`/facts-center/query/frontend/live-matches/count?sportId=${sportId}`)
}

/**
 * 获取目标运动相关比赛个数
 * @param  {String} sportId   目标 sport id
 * @return {Object}           [description]
 */
export const fetchMatchsStatistics = (sportId = '', force = false) => {
  return cacheResponse(`matchsCountStatistics-${sportId}`, xfetch(`/facts-center/query/frontend/fastEntryToSports/matchsCountStatistics?sportId=${sportId}`), {
    type: 'session',
    maxAge: 1000 * 60 * 5,
    force
  })
}

/**
 * Fetches live betting's matches for a given sport.
 *
 * @param {string} [sportId=''] - The ID of the sport to fetch matches for. Defaults to an empty string.
 * @returns {Promise<Response>} A promise that resolves to the response of the fetch request.
 */
export const fetchLiveMatches = (sportId = '', exceptEventIds = '') => {
  const params = new URLSearchParams({ sportId })
  if (exceptEventIds) {
    params.append('exceptEventIds', exceptEventIds)
  }

  const key = 'homeLiveMatchs'
  controller.abort(key)
  const signal = controller.create(key)

  return xfetch(`/facts-center/query/frontend/live-matches?${params}`, { signal })
}

/**
 * Fetches highlight's matches for a given sport.
 *
 * @param {string} [sportId=''] - The ID of the sport to fetch matches for. Defaults to an empty string.
 * @returns {Promise<Response>} A promise that resolves to the response of the fetch request.
 */
export const fetchHighlightMatches = (sportId = '') => {
  const key = 'homeHighlightMatchs'
  controller.abort(key)
  const signal = controller.create(key)
  return xfetch(`/facts-center/query/frontend/soccer-matches/high-light?sportId=${sportId}`, { signal })
}

/**
 * Fetches matches for the next 7 days for a given sport.
 *
 * @param {string} [sportId=''] - The ID of the sport to fetch matches for. Defaults to an empty string.
 * @returns {Promise} - A promise that resolves to the response of the fetch request.
 */
export const fetchNext7DaysMatches = (sportId = '') => {
  const key = 'homeNext7DaysMatches'
  controller.abort(key)
  const signal = controller.create(key)
  return xfetch(`/facts-center/query/frontend/all-matches/next-7-days?sportId=${sportId}`, { signal })
}

/**
 * Fetches today's matches for a given sport.
 *
 * @param {string} [sportId=''] - The ID of the sport to fetch matches for. Defaults to an empty string.
 * @returns {Promise<Response>} A promise that resolves to the response of the fetch request.
 */
export const fetchTodayMatches = (sportId = '') => {
  const key = 'homeTodayMatchs'
  controller.abort(key)
  const signal = controller.create(key)
  return xfetch(`/facts-center/query/frontend/all-matches/today?sportId=${sportId}`, { signal })
}

/**
 * Fetches the count of today's matches for a given sport.
 *
 * @param {string} [sportId=''] - The ID of the sport to fetch the match count for. Defaults to an empty string.
 * @returns {Promise} A promise that resolves to the count of today's matches for the specified sport.
 */
export const fetchTodayMatchesCount = (sportId = '') => {
  const key = 'homeTodayCount'
  controller.abort(key)
  const signal = controller.create(key)
  return xfetch(`/facts-center/query/frontend/today-games/count?sportId=${sportId}`, { signal })
}

/**
 * Fetches top leagues's matches for a given sport.
 *
 * @param {string} [sportId=''] - The ID of the sport to fetch matches for. Defaults to an empty string.
 * @returns {Promise<Response>} A promise that resolves to the response of the fetch request.
 */
export const fetchTopLeagues = (sportId = '') => {
  const key = 'homeTopLeagues'
  controller.abort(key)
  const signal = controller.create(key)
  return xfetch(`/facts-center/query/frontend/all-matches/top-league?sportId=${sportId}`, { signal })
}

/**
 * 获取 favourites leagues
 * @return {Promise}              接口响应 data
 */
export const fetchFavouriteLeagues = () => axios.get('/facts-center/query/frontend/my-favourites/tournaments')

/**
 * 添加/取消收藏 league
 * @param  {String}  tournamentId league id
 * @param  {Boolean} status       添加或取消的状态，true 为收藏，false 为取消收藏
 * @return {Promise}              接口响应 data
 */
export const updateFavouriteLeague = (tournamentId, status = true) => {
  const method = status ? 'put' : 'delete'
  return axios[method](`/facts-center/query/frontend/my-favourites/tournaments?tournamentId=${tournamentId}`)
}

/**
 * 获取比赛 Countries 列表, 不填默认sr:sport:1，足球
 * @param {String} sportId  不填默认sr:sport:1，足球
 * @returns {Object}
 */
export const fetchMatchCountries = (sportId = '') => {
  const params = new URLSearchParams()
  if (sportId) params.append('sportId', sportId)
  return xfetch(`/facts-center/query/frontend/all-matches/countries?${params}`)
}
